// Hidden crypt page - visitors who never left
import hidden from '../../content/hidden.json';
import { glitchText, addScanlines, addCRTFlicker } from '../../scripts/effects';
import { appendReturnToHouse } from '../../scripts/navigation';

interface CryptEntry {
  name: string;
  checked_in: string;
  checked_out: string;
  room: string;
  notes: string;
}

/**
 * Get status class based on checkout status
 */
function getStatusClass(checkedOut: string): string {
  switch (checkedOut.toUpperCase()) {
    case 'PENDING':
      return 'crypt-status--pending';
    case 'NEVER':
      return 'crypt-status--never';
    case 'DISPUTED':
      return 'crypt-status--disputed';
    default:
      if (checkedOut.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return 'crypt-status--dated';
      }
      return 'crypt-status--unknown';
  }
}

/**
 * Render the hidden crypt page
 * Displays visitors who checked in but never left
 */
export function renderCrypt(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'hidden-container crypt-container';

  // Get crypt data with proper typing
  const cryptData = (hidden as any).crypt;
  const title = cryptData?.title || 'THE CRYPT';
  const subtitle = cryptData?.subtitle || 'VISITORS WHO NEVER LEFT';
  const entries: CryptEntry[] = cryptData?.entries || [];
  const epitaph = cryptData?.epitaph || 'VACANCY: 0 / CAPACITY: ∞';

  // Build entries HTML
  const entriesList = entries
    .map(
      (entry, index) => `
      <article class="crypt-entry" data-status="${entry.checked_out}" role="listitem" aria-label="Entry ${index + 1}: ${entry.name}">
        <header class="crypt-entry-header">
          <span class="crypt-entry-name">${entry.name}</span>
          <span class="crypt-entry-room">[${entry.room}]</span>
        </header>
        <div class="crypt-entry-dates">
          <div class="crypt-date-row">
            <span class="crypt-label">CHECKED IN:</span>
            <span class="crypt-date-in">${entry.checked_in}</span>
          </div>
          <div class="crypt-date-row">
            <span class="crypt-label">CHECKED OUT:</span>
            <span class="crypt-date-out ${getStatusClass(entry.checked_out)}">${entry.checked_out}</span>
          </div>
        </div>
        <div class="crypt-entry-notes">
          <p>${entry.notes}</p>
        </div>
      </article>
    `
    )
    .join('<div class="crypt-divider" aria-hidden="true">. . . . . . . . . . . . . . . . . . . .</div>');

  container.innerHTML = `
    <!--
      ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
      ▓  CRYPT ACCESS GRANTED                                        ▓
      ▓                                                              ▓
      ▓  You found the crypt.                                        ▓
      ▓  Some guests never check out.                                ▓
      ▓                                                              ▓
      ▓  Other hidden rooms:                                         ▓
      ▓    /basement - AI narrator fragments                         ▓
      ▓    /attic    - Lost manuscript pages                         ▓
      ▓    /archive  - Sealed BBS threads                            ▓
      ▓    /market   - Forbidden editions                            ▓
      ▓                                                              ▓
      ▓  Check your progress: __chopras.status()                     ▓
      ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
    -->
    <div class="crypt-terminal" role="main" aria-label="Crypt - Hidden room containing visitor records">
      <header class="hidden-header crypt-header" id="main-content" tabindex="-1">
        <div class="crypt-header-main">> ${title}</div>
        <div class="hidden-subheader crypt-subheader">> ${subtitle}</div>
      </header>

      <div class="crypt-system-info">
        <span class="crypt-system-line">TOTAL ENTRIES: ${entries.length}</span>
        <span class="crypt-system-line">STATUS: ACCEPTING NEW GUESTS</span>
        <span class="crypt-system-line">${epitaph}</span>
      </div>

      <div class="crypt-divider-main" aria-hidden="true">═══════════════════════════════════════════════════════</div>

      <div class="crypt-warning-banner">
        <span class="crypt-warning-icon">†</span>
        <span class="crypt-warning-text">CHECKOUT IS NOT GUARANTEED. THE HOUSE DECIDES.</span>
      </div>

      <div class="crypt-entries" role="list" aria-label="Visitor records">
        ${entriesList}
      </div>

      <div class="crypt-footer">
        <div class="crypt-epitaph">
          <span class="crypt-epitaph-text">"He wanted you to stay."</span>
        </div>
      </div>

      <nav class="hidden-nav crypt-nav"></nav>
    </div>
  `;

  app.appendChild(container);

  // Add CRT effects to terminal
  const terminal = container.querySelector('.crypt-terminal');
  if (terminal) {
    addCRTFlicker(terminal as HTMLElement);
    addScanlines(terminal as HTMLElement);
  }

  // Add return to house navigation
  const navContainer = container.querySelector('.crypt-nav');
  if (navContainer) {
    appendReturnToHouse(navContainer as HTMLElement, { className: '' });
  }

  // Apply subtle glitch effects
  setupCryptEffects(container);
}

/**
 * Setup glitch effects for crypt entries
 */
function setupCryptEffects(container: HTMLElement): void {
  const notes = container.querySelectorAll('.crypt-entry-notes p');

  notes.forEach((note, index) => {
    // Periodic glitch on notes
    setTimeout(() => {
      setInterval(() => {
        glitchText(note as HTMLElement, 250);
      }, 5000 + Math.random() * 5000);
    }, index * 800);
  });

  // Special effect for "[YOUR NAME HERE]" entry
  const yourEntry = container.querySelector('.crypt-entry[data-status="NEVER"]');
  if (yourEntry) {
    const nameEl = yourEntry.querySelector('.crypt-entry-name') as HTMLElement;
    if (nameEl) {
      setInterval(() => {
        glitchText(nameEl, 400);
      }, 3000);
    }
  }

  // Random glitch on dates
  const dates = container.querySelectorAll('.crypt-date-out');
  setInterval(() => {
    const randomIndex = Math.floor(Math.random() * dates.length);
    const date = dates[randomIndex] as HTMLElement;
    if (date) {
      glitchText(date, 150);
    }
  }, 4000);
}

/**
 * Cleanup crypt page
 */
export function cleanupCrypt(): void {
  // No active loops to clean up
}
